import { useEffect, useMemo, useRef, useState } from "react";
import { levelInfo, levelTitle } from "../lib/xp";
import { AVATAR_COLORS, AVATAR_EMOJIS } from "../lib/profile";
import { fileToAvatar } from "../lib/image";
import { useMuted } from "../lib/sounds";
import { useTheme } from "../lib/theme";
import { useT } from "../i18n";
import Icon from "./icons";
import Pic from "./pics";

export function Avatar({ avatar, size = 44, crown, level, online = true, className = "" }) {
  const a = avatar || { emoji: "🙂", color: "#ccc" };
  return (
    <div
      className={`avatar ${online ? "" : "avatar-offline"} ${className}`}
      style={{ background: a.color, width: size, height: size, fontSize: size * 0.55 }}
    >
      {a.image ? <img className="avatar-img" src={a.image} alt="" draggable={false} /> : <span>{a.emoji}</span>}
      {crown && <span className="avatar-crown"><Pic name="crown" /></span>}
      {level ? <span className="avatar-level">{level}</span> : null}
    </div>
  );
}

export function AvatarPicker({ value, onChange }) {
  const t = useT();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      onChange({ ...value, image: await fileToAvatar(file) });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Picking an emoji switches back from a photo.
  const pickEmoji = (emoji) => {
    const { image, ...rest } = value;
    onChange({ ...rest, emoji });
  };

  return (
    <div className="avatar-picker">
      <div className="avatar-upload-row">
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} />
        <button type="button" className="btn btn-sm" onClick={() => fileRef.current?.click()} disabled={busy}>
          <Pic name="camera" /> {busy ? t("Processing…") : value.image ? t("Change photo") : t("Upload photo")}
        </button>
        {value.image && (
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => pickEmoji(value.emoji || "🙂")}>
            {t("Remove photo")}
          </button>
        )}
        {error && <span className="field-error">{error}</span>}
      </div>
      <div className="avatar-picker-row">
        {AVATAR_EMOJIS.map((e) => (
          <button
            key={e}
            type="button"
            className={`emoji-choice ${!value.image && value.emoji === e ? "selected" : ""}`}
            onClick={() => pickEmoji(e)}
          >
            {e}
          </button>
        ))}
      </div>
      <div className="avatar-picker-row">
        {AVATAR_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={t("Colour {color}", { color: c })}
            className={`color-choice ${value.color === c ? "selected" : ""}`}
            style={{ background: c }}
            onClick={() => onChange({ ...value, color: c })}
          />
        ))}
      </div>
    </div>
  );
}

export function XPBar({ xp, compact }) {
  const t = useT();
  const info = levelInfo(xp);
  return (
    <div className={`xpbar ${compact ? "xpbar-compact" : ""}`}>
      {!compact && (
        <div className="xpbar-labels">
          <span className="xpbar-level">
            {t("Lv {level}", { level: info.level })} · <em>{t(levelTitle(info.level))}</em>
          </span>
          <span className="xpbar-num">
            {info.into} / {info.needed} XP
          </span>
        </div>
      )}
      <div className="xpbar-track">
        <div className="xpbar-fill" style={{ width: `${Math.round(info.pct * 100)}%` }} />
      </div>
    </div>
  );
}

export function Modal({ open, onClose, children, className = "", title }) {
  const t = useT();
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={`modal ${className}`} role="dialog" aria-modal="true" aria-label={title}>
        {onClose && (
          <button className="modal-close" onClick={onClose} aria-label={t("Close")}>
            ×
          </button>
        )}
        {children}
      </div>
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <div className="spinner-wrap">
      <div className="spinner">
        <span><Pic name="cards" /></span>
      </div>
      {label && <p>{label}</p>}
    </div>
  );
}

const CONFETTI_COLORS = ["#ff6b9d", "#ffb84d", "#ffe156", "#6bdc8a", "#4dd4ff", "#7c5cff"];
// `delay` (seconds) holds it back, e.g. until a reveal gets to the good part.
export function Confetti({ count = 80, delay = 0 }) {
  const pieces = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        left: Math.random() * 100,
        delay: delay + Math.random() * 1.5,
        dur: 2.5 + Math.random() * 2,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        rot: Math.random() * 360,
        size: 6 + Math.random() * 8,
      })),
    [count, delay]
  );
  return (
    <div className="confetti" aria-hidden>
      {pieces.map((p, i) => (
        <i
          key={i}
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            background: p.color,
            width: p.size,
            height: p.size * 0.5,
            transform: `rotate(${p.rot}deg)`,
          }}
        />
      ))}
    </div>
  );
}

// The 🔥 Most popular tag on a game (see lib/popular.js).
export function PopularTag() {
  const t = useT();
  return (
    <span className="popular-tag">
      <Pic name="fire" /> {t("Most popular")}
    </span>
  );
}

export function Countdown({ endsAt, now, total }) {
  const left = Math.max(0, Math.ceil((endsAt - now) / 1000));
  const pct = total ? Math.max(0, Math.min(1, (endsAt - now) / (total * 1000))) : 1;
  return (
    <div className={`countdown ${left <= 10 ? "countdown-urgent" : ""} ${left <= 5 && left > 0 ? "countdown-panic" : ""}`}>
      <svg viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="15.5" className="countdown-bg" />
        <circle
          cx="18"
          cy="18"
          r="15.5"
          className="countdown-fg"
          strokeDasharray={`${pct * 97.4} 97.4`}
        />
      </svg>
      <span>{left}</span>
    </div>
  );
}

export function SoundToggle() {
  const t = useT();
  const [muted, setMuted] = useMuted();
  return (
    <button
      className="btn btn-sm btn-ghost sound-toggle"
      data-sound="none"
      onClick={() => setMuted(!muted)}
      title={muted ? t("Turn sounds on") : t("Turn sounds off")}
      aria-label={muted ? t("Turn sounds on") : t("Turn sounds off")}
    >
      <Pic name={muted ? "soundOff" : "soundOn"} />
    </button>
  );
}

// Light / dark switch in the header. Shows what you'd switch to.
export function ThemeToggle() {
  const t = useT();
  const [theme, setTheme] = useTheme();
  const dark = theme === "dark";
  const label = dark ? t("Switch to light mode") : t("Switch to dark mode");
  return (
    <button className="btn btn-sm btn-ghost theme-toggle" onClick={() => setTheme(dark ? "light" : "dark")} title={label} aria-label={label}>
      <Icon name={dark ? "sun" : "moon"} />
    </button>
  );
}

export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};
